<template>
  <v-dialog v-model="open" max-width="720">
    <v-card class="rounded-lg">
      <v-card-title class="d-flex align-center">
        <div class="min-w-0">
          <v-card-title class="align-start pa-0">
            <div class="event-title">
              {{ event?.title || event?.name || $t("common.event") }}
       
            </div>
          </v-card-title>

          <div class="text-caption grey--text">
            {{ dateLine }}
          </div>
        </div>
      </v-card-title>

      <v-card-text>
        <div v-if="event?.description" class="mb-4">
          {{ event.description }}
        </div>

        <v-divider class="my-4" />

        <div class="text-subtitle-2 font-weight-bold mb-2">
          {{ $t("eventDetails.participatingGroups") }}
        </div>

        <v-div
          v-if="participateGroupIds.length === 0"
          type="info"
          outlined
          class="mb-3"
        >
          -
        </v-div>

        <!-- Chips de grupos participantes -->
        <div v-else class="d-flex flex-wrap mb-2" style="gap: 8px">
          <v-chip
            v-for="gid in participateGroupIds"
            :key="'p-' + gid"
            class="rounded-pill"
            color="primary"
            text-color="white"
            small
          >
            <v-icon left small>mdi-account-check</v-icon>
            {{ groupLabelById(gid) }}
          </v-chip>
        </div>

        <!-- Estado por miembro, agrupado por grupo -->
        <div v-if="participateGroupIds.length">
          <v-skeleton-loader
            v-if="loadingStatuses"
            type="list-item-three-line, list-item-three-line, list-item-three-line"
          />

          <div v-else>
            <div
              v-for="gid in participateGroupIds"
              :key="'status-' + gid"
              class="mb-4"
            >
              <div class="d-flex align-center mb-2">
                <div class="text-subtitle-2 font-weight-bold">
                  {{ groupLabelById(gid) }}
                </div>
                <v-spacer />
                <div
                  class="text-caption grey--text"
                  v-if="groupMembersStatus[gid]"
                >
                  {{ $t("eventDetails.membersCount", { count: groupMembersStatus[gid].length }) }}
                </div>
              </div>

              <v-alert
                v-if="
                  !groupMembersStatus[gid] ||
                  groupMembersStatus[gid].length === 0
                "
                type="info"
                outlined
                dense
              >
                {{ $t("eventDetails.noActiveMembers") }}
              </v-alert>

              <v-list v-else dense class="pa-0">
                <v-list-item
                  v-for="m in groupMembersStatus[gid]"
                  :key="gid + '-' + m.uid"
                  class="px-0"
                >
                  <v-list-item-content class="py-1">
                    <v-list-item-title class="text-body-2">
                      {{ m.displayName }}
                      <span
                        v-if="m.email"
                        class="grey--text text--darken-1 text-caption"
                      >
                        · {{ m.email }}
                      </span>
                      <span
                        v-if="m.uid === uid"
                        class="grey--text text--darken-1 text-caption"
                      >
                        · {{ $t("eventDetails.you") }}
                      </span>
                    </v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-action class="ma-0">
                    <v-chip
                      x-small
                      class="rounded-pill"
                      :color="statusColor(m.inviteStatus)"
                      :outlined="
                        m.inviteStatus === 'pending' ||
                        m.inviteStatus === 'unknown'
                      "
                      :text-color="
                        m.inviteStatus === 'pending' ||
                        m.inviteStatus === 'unknown'
                          ? 'grey darken-3'
                          : 'white'
                      "
                    >
                      <v-icon left x-small>
                        {{ statusIcon(m.inviteStatus) }}
                      </v-icon>
                      {{ statusLabel(m.inviteStatus) }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>

              <v-divider class="mt-3" />
            </div>
          </div>
        </div>

        <v-divider class="my-4" />

        <div class="text-subtitle-2 font-weight-bold mb-2">
          {{ $t("eventDetails.viewingGroups") }}
        </div>

        <v-div
          v-if="viewGroupIds.length === 0"
          type="info"
          outlined
          class="mb-3"
        >
          -
        </v-div>

        <div v-else class="d-flex flex-wrap" style="gap: 8px">
          <v-chip
            v-for="gid in viewGroupIds"
            :key="'v-' + gid"
            class="rounded-pill"
            outlined
            color="grey"
            small
          >
            <v-icon left small>mdi-eye</v-icon>
            {{ groupLabelById(gid) }}
          </v-chip>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn v-if="canEdit" color="primary" text @click="$emit('edit', event)">
          <v-icon left small>mdi-pencil</v-icon>
          {{ $t("calendar.editEvent") }}
        </v-btn>
        <v-btn text @click="open = false">{{ $t("common.close") }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { doc, getDoc, getDocs, collection } from "firebase/firestore";
import { db } from "@/services/firebase";

function toDate(ts) {
  if (ts?.toDate) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return ts ? new Date(ts) : null;
}

export default {
  name: "EventDetailsDialog",
  props: {
    value: { type: Boolean, default: false }, // v-model (Vuetify 2)
    event: { type: Object, default: null },
    groups: { type: Array, default: () => [] },
    uid: { type: String, default: "" },
    canEdit: { type: Boolean, default: false },
  },

  data() {
    return {
      loadingStatuses: false,
      // { [groupId]: [{ uid, displayName, email, inviteStatus }] }
      groupMembersStatus: {},
    };
  },

  computed: {
    open: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },

    groupById() {
      const map = {};
      (this.groups || []).forEach((g) => {
        const id = String(g.id || g.groupId || "");
        if (id) map[id] = g;
      });
      return map;
    },

    shareByGroup() {
      return this.event && this.event.shareByGroup
        ? this.event.shareByGroup
        : {};
    },

    groupIds() {
      return Array.isArray(this.event?.groupIds)
        ? this.event.groupIds.map(String)
        : [];
    },

    participateGroupIds() {
      return this.groupIds.filter(
        (gid) => this.shareByGroup[String(gid)] === "participate",
      );
    },

    viewGroupIds() {
      return this.groupIds.filter(
        (gid) => this.shareByGroup[String(gid)] === "view",
      );
    },

    dateLine() {
      const s = toDate(this.event?.startAt);
      const e = toDate(this.event?.endAt);

      if (!s) return "";
      const optsD = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      const optsT = { hour: "2-digit", minute: "2-digit" };

      const d1 = s.toLocaleDateString(this.$i18n.locale, optsD);
      const t1 = s.toLocaleTimeString(this.$i18n.locale, optsT);

      if (!e) return `${d1} · ${t1}`;

      const sameDay =
        s.getFullYear() === e.getFullYear() &&
        s.getMonth() === e.getMonth() &&
        s.getDate() === e.getDate();

      const t2 = e.toLocaleTimeString(this.$i18n.locale, optsT);

      if (sameDay) return `${d1} · ${t1}–${t2}`;

      const d2 = e.toLocaleDateString(this.$i18n.locale, optsD);
      return `${d1} ${t1} → ${d2} ${t2}`;
    },
  },

  watch: {
    open: {
      immediate: true,
      async handler(v) {
        if (v) await this.loadStatusesByMember();
      },
    },
    // si cambias de evento con el dialog abierto
    event: {
      deep: false,
      async handler() {
        if (this.open) await this.loadStatusesByMember();
      },
    },
  },

  methods: {
    groupLabelById(gid) {
      const g = this.groupById[String(gid)];
      return g?.name || this.$t("calendar.groupFallback", { id: gid });
    },

    statusLabel(st) {
      if (st === "pending") return this.$t("eventDetails.statusPending");
      if (st === "declined") return this.$t("eventDetails.statusDeclined");
      if (st === "accepted") return this.$t("eventDetails.statusAccepted");
      return this.$t("eventDetails.statusUnknown");
    },

    statusIcon(st) {
      if (st === "accepted") return "mdi-check-circle";
      if (st === "declined") return "mdi-close-circle";
      if (st === "pending") return "mdi-clock-outline";
      return "mdi-help-circle-outline";
    },

    statusColor(st) {
      if (st === "accepted") return "primary";
      if (st === "declined") return "error";
      if (st === "pending") return "grey";
      return "grey";
    },

    async loadStatusesByMember() {
      this.groupMembersStatus = {};
      if (!this.uid || !this.event?.id) return;
      if (!this.participateGroupIds.length) return;

      this.loadingStatuses = true;

      try {
        // Para cada grupo participante -> cargar miembros -> para cada miembro -> cargar invite status de este evento
        const perGroup = await Promise.all(
          this.participateGroupIds.map(async (gid) => {
            const groupId = String(gid);

            // 1) miembros del grupo
            const membersSnap = await getDocs(
              collection(db, "groups", groupId, "members"),
            );

            const membersRaw = [];
            membersSnap.forEach((d) => {
              const uid = d.id;
              const data = d.data() || {};

              membersRaw.push({
                uid,
                displayName: data.displayName || data.name || "",
                email: data.email || "",
              });
            });

            // 1b) Si faltan nombres/emails, los sacamos de users/{uid}
            const members = await Promise.all(
              membersRaw.map(async (m) => {
                if (m.displayName || m.email) return m;

                try {
                  const uSnap = await getDoc(doc(db, "users", m.uid));
                  if (uSnap.exists()) {
                    const u = uSnap.data() || {};
                    return {
                      ...m,
                      displayName:
                        u.displayName ||
                        (u.email ? u.email.split("@")[0] : "") ||
                        m.uid,
                      email: u.email || "",
                    };
                  }
                  return { ...m, displayName: m.uid };
                } catch (e) {
                  console.error(e);
                  return { ...m, displayName: m.uid };
                }
              }),
            );

            // 2) invite status por miembro (users/{uid}/eventInvites/{eventId})
            const withStatuses = await Promise.all(
              members.map(async (m) => {
                // si es el creador, siempre aceptado
                if (this.event?.createdBy && m.uid === this.event.createdBy) {
                  return { ...m, inviteStatus: "accepted" };
                }

                const invRef = doc(
                  db,
                  "users",
                  m.uid,
                  "eventInvites",
                  this.event.id,
                );
                const invSnap = await getDoc(invRef);

                if (invSnap.exists()) {
                  const inv = invSnap.data() || {};
                  return { ...m, inviteStatus: inv.status || "unknown" };
                }

                // si NO hay invite:
                // - para grupos "view" normalmente es accepted (entran directos por eventAccess)
                // - para "participate" debería existir invite, pero si falta, lo mostramos unknown
                return { ...m, inviteStatus: "unknown" };
              }),
            );

            // opcional: ordenar (tú primero arriba, luego aceptados, pendientes, rechazados)
            const rank = (st) => {
              if (st === "accepted") return 1;
              if (st === "pending") return 2;
              if (st === "unknown") return 3;
              if (st === "declined") return 4;
              return 9;
            };

            withStatuses.sort((a, b) => {
              if (a.uid === this.uid && b.uid !== this.uid) return -1;
              if (b.uid === this.uid && a.uid !== this.uid) return 1;
              const ra = rank(a.inviteStatus);
              const rb = rank(b.inviteStatus);
              if (ra !== rb) return ra - rb;
              return (a.displayName || "").localeCompare(
                b.displayName || "",
                this.$i18n.locale,
              );
            });

            return [groupId, withStatuses];
          }),
        );

        const out = {};
        perGroup.forEach(([gid, arr]) => {
          out[gid] = arr;
        });
        this.groupMembersStatus = out;
      } finally {
        this.loadingStatuses = false;
      }
    },
  },
};
</script>

<style scoped>
.event-title {
  font-size: 1.25rem;   /* equivalente a text-h6 */
  font-weight: 600;
  line-height: 1.15;    /* 🔥 compacta las líneas */
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  margin: 0;            /* importantísimo */
}

</style>
